'use client';

import { useEffect, useRef, useState } from 'react';
import { SectLabel, SectLede } from '@/components/landing/v4-shared';
import { AI_BUSINESS_PAGE_COPY } from '@/lib/marketing/messaging-bank';

const TARGET = 58;
const DURATION_MS = 1200;

export function ProblemStat() {
  const [value, setValue] = useState(0);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const startedRef = useRef(false);
  const section = AI_BUSINESS_PAGE_COPY.sections.find((s) => s.id === 'problem');

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setValue(TARGET);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting || startedRef.current) continue;
          startedRef.current = true;
          const start = performance.now();
          const tick = (now: number) => {
            const t = Math.min(1, (now - start) / DURATION_MS);
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(Math.round(eased * TARGET));
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  if (!section) return null;

  return (
    <section className="py-28 md:py-36 relative">
      <div
        className="v4-aibp-section-rule absolute top-0 inset-x-0"
        aria-hidden="true"
      />
      <div ref={sectionRef} className="max-w-3xl mx-auto px-6 text-center">
        <div className="mb-8 flex justify-center">
          <SectLabel color="var(--amber-glow)">{section.eyebrow}</SectLabel>
        </div>

        <div
          className="v4-aibp-speakable-stat"
          style={{
            fontFamily: 'var(--font-geist-mono)',
            fontWeight: 600,
            letterSpacing: '-0.04em',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 0.85,
            color: 'transparent',
            backgroundImage:
              'linear-gradient(180deg, var(--paper) 0%, color-mix(in srgb, var(--paper) 35%, transparent) 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            fontSize: 'clamp(96px, 18vw, 200px)',
            filter: 'drop-shadow(0 30px 80px rgba(245,165,36,0.22))',
          }}
          aria-label={`${TARGET} percent`}
        >
          {value}
          <span
            style={{
              color: 'var(--amber)',
              backgroundImage: 'none',
              WebkitTextFillColor: 'currentColor',
            }}
          >
            %
          </span>
        </div>

        <h2
          style={{
            marginTop: '40px',
            fontFamily: 'var(--font-geist-sans)',
            fontSize: 'clamp(24px, 3vw, 36px)',
            fontWeight: 500,
            lineHeight: 1.2,
            color: 'var(--paper)',
            letterSpacing: '-0.02em',
            maxWidth: '32ch',
            margin: '40px auto 0',
          }}
        >
          {section.heading}
        </h2>

        <div className="mt-6 mx-auto" style={{ maxWidth: '720px' }}>
          <SectLede style={{ margin: '0 auto', textAlign: 'center' }}>
            {section.body}
          </SectLede>
        </div>
      </div>
    </section>
  );
}
